"use client";
import { Drawer as DrawerPrimitive } from "vaul";
import { useViewportSize } from "@mantine/hooks";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from "./sheet";
import { createContext, forwardRef, Fragment, useContext } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";

const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  breakpoint?: keyof typeof breakpoints;
};

type DrawerType = "drawer" | "sheet";
const DrawerContext = createContext<DrawerType>("drawer");

const useDrawerType = () => {
  return useContext(DrawerContext);
};

export const AdaptiveDrawer = ({
  breakpoint = "lg",
  children,
  ...props
}: DrawerProps) => {
  const { width } = useViewportSize();

  const bp = breakpoints[breakpoint];
  const drawer = !!width ? (width < bp ? "drawer" : "sheet") : "drawer";

  return (
    <Fragment>
      <DrawerContext.Provider value={drawer}>
        {drawer === "drawer" ? (
          <Drawer {...props}>{children}</Drawer>
        ) : (
          <Sheet {...props}>{children}</Sheet>
        )}
      </DrawerContext.Provider>
    </Fragment>
  );
};

export const AdaptiveDrawerTrigger = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof DrawerPrimitive.Trigger>
>((props, ref) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetTrigger ref={ref} {...props} />;
  }

  return <DrawerTrigger ref={ref} {...props} />;
});

AdaptiveDrawerTrigger.displayName = "AdaptiveDrawerTrigger";

export const AdaptiveDrawerPortal = (
  { children, ...props }: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Portal>,
) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetPortal {...props}>{children}</SheetPortal>;
  }

  return <DrawerPortal {...props}>{children}</DrawerPortal>;
};

export const AdaptiveDrawerClose = (
  props: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Close>,
) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetClose {...props} />;
  }

  return <DrawerClose {...props} />;
};

export const AdaptiveDrawerOverlay = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>((props, ref) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetOverlay ref={ref} {...props} />;
  }

  return <DrawerOverlay ref={ref} {...props} />;
});

AdaptiveDrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

export const AdaptiveDrawerContent = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ children, ...props }, ref) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return (
      <SheetContent side="right" {...props}>
        {children}
      </SheetContent>
    );
  }

  return (
    <DrawerContent ref={ref} {...props}>
      {children}
    </DrawerContent>
  );
});

AdaptiveDrawerContent.displayName = DrawerPrimitive.Content.displayName;

export const AdaptiveDrawerHeader = (
  props: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>,
) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetHeader {...props} />;
  }

  return <DrawerHeader {...props} />;
};

export const AdaptiveDrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetFooter {...props} />;
  }

  return <DrawerFooter {...props} />;
};

export const AdaptiveDrawerTitle = forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>((props, ref) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetTitle ref={ref} {...props} />;
  }

  return <DrawerTitle ref={ref} {...props} />;
});

AdaptiveDrawerTitle.displayName = DrawerPrimitive.Title.displayName;

export const AdaptiveDrawerDescription = forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>((props, ref) => {
  const drawerType = useDrawerType();

  if (drawerType === "sheet") {
    return <SheetDescription ref={ref} {...props} />;
  }

  return <DrawerPrimitive.Description ref={ref} {...props} />;
});

AdaptiveDrawerDescription.displayName = DrawerPrimitive.Description.displayName;
